import React, { useEffect, useState } from "react";
import moment from "moment";
import Button from "../../../shared/components/Button/Button";
import { categories } from "../../../data/product";
import {
  DELIVERY_TYPES,
  NCM_SUGGESTIONS,
  isValidNcm,
  normalizeNcm,
} from "../utils/fiscal";

const ORIGENS = [
  { value: 0, label: "0 — Nacional" },
  { value: 1, label: "1 — Estrangeira (importação direta)" },
  { value: 2, label: "2 — Estrangeira (adquirida no mercado interno)" },
];

export const missingNcmCategories = (ncmByCategory) =>
  categories.filter((c) => !isValidNcm(ncmByCategory?.[c.value]));

/**
 * Configuração fiscal (NCM por categoria, origem, peso, natureza de operação)
 * e da emissão automática de NF-e.
 */
const BlingFiscalForm = ({ config, naturezas = [], saving, onSave }) => {
  const [draft, setDraft] = useState(config);

  useEffect(() => setDraft(config), [config]);

  if (!draft) return null;

  const set = (field, value) => setDraft((d) => ({ ...d, [field]: value }));
  const setNcm = (category, value) =>
    set("ncmByCategory", {
      ...(draft.ncmByCategory || {}),
      [category]: normalizeNcm(value),
    });

  const fillSuggestions = () => {
    const next = { ...(draft.ncmByCategory || {}) };
    categories.forEach((c) => {
      if (!isValidNcm(next[c.value]) && NCM_SUGGESTIONS[c.value])
        next[c.value] = NCM_SUGGESTIONS[c.value];
    });
    set("ncmByCategory", next);
  };

  const toggleType = (type) => {
    const types = draft.autoEmitDeliveryTypes || [];
    set(
      "autoEmitDeliveryTypes",
      types.includes(type) ? types.filter((t) => t !== type) : [...types, type],
    );
  };

  const missing = missingNcmCategories(draft.ncmByCategory);
  // Sem NCM em todas as categorias a nota seria rejeitada — não deixa ligar a automação.
  const canEnableAuto = missing.length === 0;

  const submit = (e) => {
    e.preventDefault();
    onSave({
      ncmByCategory: draft.ncmByCategory || {},
      origem: Number(draft.origem) || 0,
      pesoBrutoKg: Number(draft.pesoBrutoKg) || 0.3,
      naturezaOperacaoId: draft.naturezaOperacaoId
        ? Number(draft.naturezaOperacaoId)
        : null,
      autoEmitEnabled: Boolean(draft.autoEmitEnabled) && canEnableAuto,
      autoEmitDeliveryTypes: draft.autoEmitDeliveryTypes || [],
    });
  };

  return (
    <form onSubmit={submit}>
      <div className="bling-page__panel mb-4">
        <div className="bling-page__row-between">
          <h5>NCM por categoria</h5>
          <Button variant="secondary" onClick={fillSuggestions}>
            Preencher sugestões
          </Button>
        </div>
        <p className="text-muted bling-page__hint">
          O NCM depende do tecido de cada peça. As sugestões são para tecido
          plano de fibra sintética — confirme com o seu contador antes de ligar
          a emissão automática.
        </p>
        <table className="bling-page__table">
          <tbody>
            {categories.map((c) => (
              <tr key={c.value}>
                <td>{c.label}</td>
                <td>
                  <input
                    aria-label={`NCM ${c.label}`}
                    className="form-control"
                    inputMode="numeric"
                    value={draft.ncmByCategory?.[c.value] || ""}
                    placeholder={NCM_SUGGESTIONS[c.value] || "8 dígitos"}
                    onChange={(e) => setNcm(c.value, e.target.value)}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        <div className="bling-page__grid">
          <label>
            Origem da mercadoria
            <select
              className="form-control"
              value={draft.origem ?? 0}
              onChange={(e) => set("origem", e.target.value)}
            >
              {ORIGENS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </label>
          <label>
            Peso bruto por peça (kg)
            <input
              className="form-control"
              type="number"
              step="0.01"
              min="0.01"
              value={draft.pesoBrutoKg ?? 0.3}
              onChange={(e) => set("pesoBrutoKg", e.target.value)}
            />
          </label>
          <label>
            Natureza de operação
            <select
              className="form-control"
              value={draft.naturezaOperacaoId || ""}
              onChange={(e) => set("naturezaOperacaoId", e.target.value)}
            >
              <option value="">Padrão de venda do Bling</option>
              {naturezas.map((n) => (
                <option key={n.id} value={n.id}>
                  {n.descricao}
                </option>
              ))}
            </select>
          </label>
        </div>
      </div>

      <div className="bling-page__panel mb-4">
        <h5>Emissão automática</h5>
        <label className="bling-page__check">
          <input
            type="checkbox"
            checked={Boolean(draft.autoEmitEnabled)}
            disabled={!canEnableAuto && !draft.autoEmitEnabled}
            onChange={(e) => set("autoEmitEnabled", e.target.checked)}
          />
          Emitir NF-e automaticamente quando o pedido for pago
        </label>
        {!canEnableAuto && (
          <p className="bling-page__warning">
            Falta NCM para: {missing.map((c) => c.label).join(", ")}.
          </p>
        )}
        {config?.autoEmitEnabled && config?.autoEmitSince && (
          <p className="text-muted bling-page__hint">
            Ativa para pedidos criados desde{" "}
            {moment(config.autoEmitSince).format("DD/MM/YYYY HH:mm")}.
          </p>
        )}
        <p className="mb-1">Tipos de entrega que geram nota:</p>
        <div className="bling-page__types">
          {DELIVERY_TYPES.map((t) => (
            <label key={t.value} className="bling-page__check">
              <input
                type="checkbox"
                checked={(draft.autoEmitDeliveryTypes || []).includes(t.value)}
                onChange={() => toggleType(t.value)}
              />
              {t.label}
            </label>
          ))}
        </div>
      </div>

      <Button type="submit" disabled={saving}>
        {saving ? "Salvando…" : "Salvar configuração"}
      </Button>
    </form>
  );
};

export default BlingFiscalForm;
